import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BE_API_AUTH_URL, BE_API_URL } from '../constants/app.constants';
import { AuthService } from './auth.service';

export interface AnnouncementPayload {
  propertyId?: string;
  propertyIds?: string[];
  companyId?: string;
  title: string;
  summary: string;
  urgency: 'normal' | 'urgent';
  validFrom?: string | Date;
  validTo?: string | Date | null;
  translations?: Record<string, { title?: string; summary?: string }>;
  attachments?: AnnouncementAttachment[];
}

export interface AnnouncementAttachment {
  clientId?: string;
  fileName: string;
  fileType: string;
  url: string;
  uploadedAt: string | Date;
  s3Key?: string;
  sourceS3Key?: string;
}

@Injectable({ providedIn: 'root' })
export class AnnouncementService {
  constructor(private http: HttpClient, private auth: AuthService) {}

  list(propertyIds?: string | string[]): Observable<any> {
    const body: { propertyId?: string; propertyIds?: string[] } = {};
    if (Array.isArray(propertyIds)) {
      const ids = propertyIds.filter(Boolean);
      if (ids.length) {
        body.propertyIds = ids;
      }
    } else if (propertyIds) {
      body.propertyId = propertyIds;
    }

    return this.http.post(`${BE_API_AUTH_URL}/announcements/search`, body, {
      headers: this.auth.createAuthorizationHeader(),
    });
  }

  create(payload: AnnouncementPayload): Observable<any> {
    return this.http.post(`${BE_API_AUTH_URL}/announcements`, payload, {
      headers: this.auth.createAuthorizationHeader(),
    });
  }

  update(id: string, payload: Partial<AnnouncementPayload>): Observable<any> {
    return this.http.put(`${BE_API_AUTH_URL}/announcements/${id}`, payload, {
      headers: this.auth.createAuthorizationHeader(),
    });
  }

  uploadAttachment(file: File): Observable<AnnouncementAttachment> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<AnnouncementAttachment>(`${BE_API_AUTH_URL}/announcements/attachments`, formData, {
      headers: this.auth.createAuthorizationHeader(),
    });
  }

  deleteAttachment(s3Key: string): Observable<any> {
    const params = new HttpParams().set('s3Key', s3Key);
    return this.http.delete(`${BE_API_AUTH_URL}/announcements/attachments`, {
      headers: this.auth.createAuthorizationHeader(),
      params,
    });
  }

  delete(id: string): Observable<any> {
    return this.http.delete(`${BE_API_AUTH_URL}/announcements/${id}`, {
      headers: this.auth.createAuthorizationHeader(),
    });
  }

  sendNow(id: string): Observable<any> {
    return this.http.post(`${BE_API_AUTH_URL}/announcements/${id}/send`, {}, {
      headers: this.auth.createAuthorizationHeader(),
    });
  }

  schedule(id: string, scheduleAt: string): Observable<any> {
    return this.http.post(`${BE_API_AUTH_URL}/announcements/${id}/schedule`, { scheduleAt }, {
      headers: this.auth.createAuthorizationHeader(),
    });
  }

  history(id: string): Observable<any> {
    return this.http.get(`${BE_API_AUTH_URL}/announcements/${id}/history`, {
      headers: this.auth.createAuthorizationHeader(),
    });
  }

  loadForLanding(companyId: string, propertyId: string): Observable<any> {
    return this.http.get(`${BE_API_URL}/properties/${companyId}/${propertyId}/announcements`);
  }

  subscribeToPush(companyId: string, propertyId: string, payload: any): Observable<any> {
    return this.http.post(`${BE_API_URL}/properties/${companyId}/${propertyId}/subscriptions`, payload);
  }

  unsubscribeFromPush(companyId: string, propertyId: string, endpoint: string): Observable<any> {
    return this.http.request('DELETE', `${BE_API_URL}/properties/${companyId}/${propertyId}/subscriptions`, {
      body: { endpoint },
    });
  }
}
